import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { answerCoverageQuestion } from "@/services/coverageMappers";
import ClaimsRcmPanel from "@/components/coverage/ClaimsRcmPanel";
import ClinicalPolicyPanel from "@/components/coverage/ClinicalPolicyPanel";
import OrthoPolicyPanel from "@/components/coverage/OrthoPolicyPanel";
import VisitReadinessPanel from "@/components/coverage/VisitReadinessPanel";
import { CitationList, SectionCard, statusBadge } from "@/components/coverage/coverageUi";
import {
  AlertTriangle,
  ArrowLeft,
  FileText,
  RefreshCw,
  Sparkles,
  Stethoscope,
  ClipboardList,
  Landmark,
} from "lucide-react";

export default function CoverageWorkspace({
  workspace,
  loading,
  verifyLoading,
  onBack,
  onVerify,
  onResolve,
}) {
  const [copilotInput, setCopilotInput] = useState("");
  const [copilotMessages, setCopilotMessages] = useState([]);
  const [correctionPath, setCorrectionPath] = useState("subscriber.date_of_birth");
  const [correctionValue, setCorrectionValue] = useState("");
  const [resolveBusy, setResolveBusy] = useState(false);
  const [audience, setAudience] = useState("visit");

  useEffect(() => {
    if (!workspace) return;
    setCopilotMessages([
      {
        role: "assistant",
        content: `Coverage Copilot is using the Coverage API payload for ${workspace.first_name}. Ask about status, deductibles, waiting periods, COB, limits, ortho, or prior auth.`,
      },
    ]);
  }, [workspace?.patient_id]);

  const sendCopilot = (e) => {
    e.preventDefault();
    if (!copilotInput.trim()) return;
    const q = copilotInput.trim();
    setCopilotMessages((prev) => [
      ...prev,
      { role: "user", content: q },
      { role: "assistant", content: answerCoverageQuestion(workspace, q) },
    ]);
    setCopilotInput("");
  };

  const handleResolve = async () => {
    if (!workspace?.review_id || !correctionValue.trim()) return;
    setResolveBusy(true);
    try {
      await onResolve(workspace.review_id, {
        appointmentId: workspace.appointment_id,
        corrections: [{ field_path: correctionPath, corrected_value: correctionValue.trim() }],
      });
    } finally {
      setResolveBusy(false);
    }
  };

  if (loading && !workspace) {
    return (
      <div className="py-24 text-center text-slate-400">
        <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-3" />
        Loading coverage workspace…
      </div>
    );
  }

  if (!workspace) {
    return (
      <div className="py-16 text-center text-slate-400">
        No coverage payload for this patient yet. Run verify.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="coverage-workspace">
      <div className="border-b border-slate-800 pb-5 space-y-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <Button
              variant="outline"
              size="sm"
              className="border-slate-800 text-slate-300 hover:bg-slate-800 text-xs py-1"
              onClick={onBack}
            >
              <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back to queue
            </Button>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-white mt-2">
              {workspace.first_name} {workspace.last_name}
            </h1>
            <p className="text-xs text-slate-400 font-mono mt-1">
              Patient {workspace.patient_id} · Member {workspace.member_id || "—"}
              {workspace.source === "postgres_cache" ? " · served from Priorath Postgres cache" : ""}
            </p>
          </div>
          <Button
            size="sm"
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold"
            onClick={onVerify}
            disabled={verifyLoading}
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${verifyLoading ? "animate-spin" : ""}`} />
            Verify coverage
          </Button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4 bg-slate-950/40 p-4 rounded-xl border border-slate-900 text-xs">
          <div>
            <span className="text-[10px] text-slate-500 uppercase font-bold">Payer</span>
            <p className="text-slate-200 font-semibold mt-0.5">{workspace.payer_name || "—"}</p>
            {workspace.payer_id ? <p className="text-[10px] text-slate-500 font-mono">{workspace.payer_id}</p> : null}
          </div>
          <div>
            <span className="text-[10px] text-slate-500 uppercase font-bold">Plan</span>
            <p className="text-slate-200 font-semibold mt-0.5">{workspace.plan_name || "—"}</p>
          </div>
          <div>
            <span className="text-[10px] text-slate-500 uppercase font-bold">Coverage</span>
            <p className="mt-0.5">{statusBadge(workspace.coverage_status)}</p>
          </div>
          <div>
            <span className="text-[10px] text-slate-500 uppercase font-bold">Readiness</span>
            <p className="mt-0.5">{statusBadge(workspace.readiness_status)}</p>
          </div>
          <div>
            <span className="text-[10px] text-slate-500 uppercase font-bold">Grounding</span>
            <p className="mt-0.5">{statusBadge(workspace.grounding_status)}</p>
          </div>
          <div>
            <span className="text-[10px] text-slate-500 uppercase font-bold">Publish</span>
            <p className="mt-0.5">{statusBadge(workspace.publish_status)}</p>
          </div>
        </div>
      </div>

      {workspace.not_a_payment_guarantee && (
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 px-4 py-3 text-xs text-slate-400 flex gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
          Coverage Intelligence is not a guarantee of payment. Confirm remaining maximums when the 271 omits them.
        </div>
      )}

      <OrthoPolicyPanel ortho={workspace.ortho} />

      <Tabs value={audience} onValueChange={setAudience}>
        <TabsList className="bg-slate-800 border border-slate-700 h-auto flex-wrap">
          <TabsTrigger
            value="visit"
            className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400"
            data-testid="coverage-tab-visit"
          >
            <ClipboardList className="w-3.5 h-3.5 mr-1.5" />
            Visit / admin
          </TabsTrigger>
          <TabsTrigger
            value="clinical"
            className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400"
            data-testid="coverage-tab-clinical"
          >
            <Stethoscope className="w-3.5 h-3.5 mr-1.5" />
            Clinical
          </TabsTrigger>
          <TabsTrigger
            value="claims"
            className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400"
            data-testid="coverage-tab-claims"
          >
            <Landmark className="w-3.5 h-3.5 mr-1.5" />
            Claims / RCM
          </TabsTrigger>
        </TabsList>
        <TabsContent value="visit" className="mt-4">
          <VisitReadinessPanel workspace={workspace} />
        </TabsContent>
        <TabsContent value="clinical" className="mt-4">
          <ClinicalPolicyPanel workspace={workspace} />
        </TabsContent>
        <TabsContent value="claims" className="mt-4">
          <ClaimsRcmPanel workspace={workspace} />
        </TabsContent>
      </Tabs>

      <SectionCard title="Citations & grounding" icon={<FileText className="w-4 h-4" />}>
        {(workspace.citations || []).length === 0 ? (
          <p className="text-sm text-slate-500">No citations on this Coverage payload.</p>
        ) : (
          <CitationList citations={workspace.citations} />
        )}
      </SectionCard>

      {workspace.review_id && (
        <Card className="bg-slate-950/40 border-amber-500/20">
          <CardHeader>
            <CardTitle className="text-amber-300 text-base">Resolve review {workspace.review_id}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <input
              className="w-full bg-slate-900 border border-slate-800 rounded-md px-3 py-2 text-sm text-white"
              value={correctionPath}
              onChange={(e) => setCorrectionPath(e.target.value)}
              placeholder="field_path"
            />
            <input
              className="w-full bg-slate-900 border border-slate-800 rounded-md px-3 py-2 text-sm text-white"
              value={correctionValue}
              onChange={(e) => setCorrectionValue(e.target.value)}
              placeholder="corrected value"
            />
            <Button
              size="sm"
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
              disabled={resolveBusy || !correctionValue.trim()}
              onClick={handleResolve}
            >
              Submit correction
            </Button>
          </CardContent>
        </Card>
      )}

      <Card className="bg-slate-950/40 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-emerald-400" /> Coverage Copilot
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 max-h-64 overflow-y-auto mb-3">
            {copilotMessages.map((m, idx) => (
              <div
                key={idx}
                className={`text-sm rounded-lg px-3 py-2 ${
                  m.role === "user" ? "bg-emerald-500/10 text-emerald-100" : "bg-slate-900 text-slate-300"
                }`}
              >
                {m.content}
              </div>
            ))}
          </div>
          <form onSubmit={sendCopilot} className="flex gap-2">
            <input
              className="flex-1 bg-slate-900 border border-slate-800 rounded-md px-3 py-2 text-sm text-white"
              value={copilotInput}
              onChange={(e) => setCopilotInput(e.target.value)}
              placeholder="Ask about this member's coverage…"
            />
            <Button type="submit" size="sm" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900">
              Send
            </Button>
          </form>
          <p className="text-[11px] text-slate-500 mt-2">
            Answers are derived from the Coverage API payload in this session. The old PostgreSQL RAG copilot is not used here.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
